import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, CSSProperties } from 'react';
import { Document, Page } from 'react-pdf';
import { pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@4.10.38/build/pdf.worker.min.mjs';

export interface PdfViewerProps {
  blob?: Blob | null;
  style?: CSSProperties;
  pageNumber?: number;
  scale?: number;
  viewMode?: 'scale' | 'fitWidth' | 'fullPage';
  onPageChange?: (pageNumber: number) => void;
  onScaleChange?: (scale: number) => void;
  onViewModeChange?: (mode: 'scale' | 'fitWidth' | 'fullPage') => void;
  onNumPagesChange?: (numPages: number) => void;
}

export const PDFViewerCore = ({
  blob,
  pageNumber,
  scale,
  viewMode,
  onPageChange,
  onScaleChange,
  onViewModeChange,
  onNumPagesChange,
  style,
}: PdfViewerProps) => {
  const [pdfData, setPdfData] = useState<ArrayBuffer | undefined>();
  const [currentScale, setCurrentScale] = useState(0.75);
  const [currentViewMode, setCurrentViewMode] = useState<'scale' | 'fitWidth' | 'fullPage'>('fitWidth');
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [viewerSize, setViewerSize] = useState({ width: 0, height: 0 });
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const ignoreScrollRef = useRef(false);
  const pendingScrollTimeoutRef = useRef<number | null>(null);
  const [viewerNode, setViewerNode] = useState<HTMLDivElement | null>(null);

  const activeViewMode = viewMode ?? currentViewMode;
  const activeScale = scale ?? currentScale;
  const activePageNumber = pageNumber ?? currentPage;

  const setActiveViewMode = (
    nextMode: 'scale' | 'fitWidth' | 'fullPage' | ((previousMode: 'scale' | 'fitWidth' | 'fullPage') => 'scale' | 'fitWidth' | 'fullPage')
  ) => {
    const mode = typeof nextMode === 'function' ? nextMode(activeViewMode) : nextMode;
    if (onViewModeChange) {
      onViewModeChange(mode);
    }
    if (viewMode === undefined) {
      setCurrentViewMode(mode);
    }
  };

  const setActiveScale = (nextScale: number | ((previousScale: number) => number)) => {
    const scaleValue = typeof nextScale === 'function' ? nextScale(activeScale) : nextScale;
    if (onScaleChange) {
      onScaleChange(scaleValue);
    }
    if (scale === undefined) {
      setCurrentScale(scaleValue);
    }
  };

  const setActivePageNumber = (nextPage: number) => {
    if (onPageChange) {
      onPageChange(nextPage);
    }
    if (pageNumber === undefined) {
      setCurrentPage(nextPage);
    }
  };

  useEffect(() => {
    if (!blob) {
      setPdfData(undefined);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setPdfData(reader.result as ArrayBuffer);
    };
    reader.readAsArrayBuffer(blob);
  }, [blob]);

  const measureViewer = useCallback(() => {
    const node = viewerNode || viewerRef.current;
    if (!node) return;
    const width = node.clientWidth || node.getBoundingClientRect().width;
    const height = node.clientHeight || node.getBoundingClientRect().height;
    setViewerSize({ width, height });
  }, [viewerNode]);

  useLayoutEffect(() => {
    const node = viewerNode || viewerRef.current;
    if (!node) return;

    const observerTarget = node.parentElement || node;
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setViewerSize({ width, height });
      }
    });
    resizeObserver.observe(observerTarget);

    window.addEventListener('resize', measureViewer);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', measureViewer);
    };
  }, [viewerNode, measureViewer]);

  useLayoutEffect(() => {
    if (!blob) return;

    measureViewer();
    const rafId = window.requestAnimationFrame(measureViewer);
    const timeoutId = window.setTimeout(measureViewer, 50);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [blob, measureViewer]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setActivePageNumber(1);
    onNumPagesChange?.(numPages);
    if (viewerRef.current) {
      viewerRef.current.scrollTop = 0;
    }
  };

  useEffect(() => {
    if (pdfData && activePageNumber) {
      if (viewerRef.current) {
        setTimeout(() => scrollToPage(activePageNumber), 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePageNumber, pdfData]);

  const updatePageNumberFromScroll = () => {
    if (!viewerRef.current) return;
    const container = viewerRef.current;

    if (ignoreScrollRef.current) {
      if (pendingScrollTimeoutRef.current) {
        window.clearTimeout(pendingScrollTimeoutRef.current);
      }
      pendingScrollTimeoutRef.current = window.setTimeout(() => {
        ignoreScrollRef.current = false;
        pendingScrollTimeoutRef.current = null;
        updatePageNumberFromScroll();
      }, 600);
      return;
    }

    const pageEls = Array.from(container.querySelectorAll<HTMLDivElement>('[data-page-number]'));
    if (!pageEls.length) return;

    if (container.scrollTop < 50) {
      if (activePageNumber !== 1) {
        setActivePageNumber(1);
      }
      return;
    }

    const containerRect = container.getBoundingClientRect();
    let bestPage = activePageNumber;
    let closestOffset = Number.POSITIVE_INFINITY;

    pageEls.forEach(el => {
      const rect = el.getBoundingClientRect();
      const offsetTop = rect.top - containerRect.top;
      if (offsetTop >= 0 && offsetTop < closestOffset) {
        closestOffset = offsetTop;
        bestPage = Number(el.dataset.pageNumber);
      }
    });

    if (bestPage !== activePageNumber) {
      setActivePageNumber(bestPage);
    }
  };

  useEffect(() => {
    const timeout = window.setTimeout(updatePageNumberFromScroll, 0);
    return () => window.clearTimeout(timeout);
  }, [numPages, pdfData]);

  const scrollToPage = (page: number) => {
    if (!viewerRef.current) {
      setActivePageNumber(page);
      return;
    }

    const target = viewerRef.current.querySelector<HTMLDivElement>(`[data-page-number="${page}"]`);
    if (target) {
      ignoreScrollRef.current = true;
      if (pendingScrollTimeoutRef.current) {
        window.clearTimeout(pendingScrollTimeoutRef.current);
      }
      pendingScrollTimeoutRef.current = window.setTimeout(() => {
        ignoreScrollRef.current = false;
        pendingScrollTimeoutRef.current = null;
        updatePageNumberFromScroll();
      }, 600);
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActivePageNumber(page);
    } else {
      setActivePageNumber(page);
    }
  };

  const defaultContainerStyle: CSSProperties = {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 210px)',
    maxHeight: 'calc(100vh - 130px)',
    minWidth: 0,
    width: '100%',
    backgroundColor: 'transparent',
    overflow: 'hidden',
  };

  if (!pdfData) return <div>Loading...</div>;

  return (
    <div style={{ ...defaultContainerStyle, ...style }}>
      <div
        style={{
          flex: 1,
          overflow: 'hidden',
          minWidth: 0,
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          position: 'relative',
          padding: '8px',
          backgroundColor: '#f8f9fb',
        }}
      >
        <div
          ref={node => {
            viewerRef.current = node;
            setViewerNode(node);
          }}
          onScroll={updatePageNumberFromScroll}
          style={{
            flex: 1,
            overflow: 'auto',
            minWidth: 0,
            width: '100%',
            paddingBottom: numPages ? '24px' : '16px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
              minWidth: 0,
              width: '100%',
            }}
          >
            <Document file={pdfData} onLoadError={e => console.warn(e)} onLoadSuccess={onDocumentLoadSuccess} externalLinkTarget="_blank">
              {numPages ? (
                Array.from({ length: numPages }, (_, index) => (
                  <div
                    key={`page_wrapper_${index + 1}`}
                    data-page-number={index + 1}
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      width: '100%',
                      padding: '6px',
                      backgroundColor: '#ffffff',
                      borderRadius: '8px',
                    }}
                  >
                    <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '8px', backgroundColor: '#fff' }}>
                      <Page
                        pageNumber={index + 1}
                        scale={activeViewMode === 'scale' ? activeScale : undefined}
                        width={activeViewMode === 'fitWidth' ? Math.max(0, viewerSize.width - 56) * activeScale : undefined}
                        height={activeViewMode === 'fullPage' ? Math.max(0, viewerSize.height - 32) * activeScale : undefined}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    padding: '6px',
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                  }}
                >
                  <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '8px', backgroundColor: '#fff' }}>
                    <Page
                      pageNumber={activePageNumber}
                      scale={activeViewMode === 'scale' ? activeScale : undefined}
                      width={activeViewMode === 'fitWidth' ? Math.max(0, viewerSize.width - 56) * activeScale : undefined}
                      height={activeViewMode === 'fullPage' ? Math.max(0, viewerSize.height - 32) * activeScale : undefined}
                    />
                  </div>
                </div>
              )}
            </Document>
          </div>
        </div>

      </div>
    </div>
  );
};
